import { AssistantRuntimeProvider } from '@assistant-ui/react';
import { useDisclosure } from '@mantine/hooks';
import { IconLayoutSidebarRightCollapseFilled, IconLayoutSidebarRightExpandFilled } from '@tabler/icons-react';
import { AnimatePresence, motion } from 'motion/react';

import { useMouseTracker } from '#core/hooks/useMouseTracker.ts';

import classes from './Chat.module.css';
import { HudBox } from '#plugin-chat/components/hud/HudBox.tsx';
import { Thread } from '#plugin-chat/components/thread/Thread.tsx';
import { ThreadList } from '#plugin-chat/components/thread-list/ThreadList.tsx';
import { TooltipIconButton } from '#plugin-chat/components/tooltip-icon-button/TooltipIconButton.tsx';
import { useAtlasChatRuntime } from '#plugin-chat/hooks/useAtlasChatRuntime.ts';
import { useLastActiveThread } from '#plugin-chat/hooks/useLastActiveThread.ts';

interface ChatProps extends React.PropsWithChildren {}

/**
 * Root chat plugin component that provides Atlas AI assistant runtime context.
 * Renders children above the chat interface to allow app content integration.
 */
export function Chat({ children }: ChatProps) {
  const runtime = useAtlasChatRuntime();

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      {children}
      <ChatContainer />
    </AssistantRuntimeProvider>
  );
}

function ChatContainer() {
  useLastActiveThread();

  // Track mouse position for potential UI interactions (tooltips, hover effects)
  const mouseTrackerRef = useMouseTracker();
  const [threadListOpen, { toggle: toggleThreadList }] = useDisclosure(false);

  const ToggleIcon = threadListOpen ? IconLayoutSidebarRightCollapseFilled : IconLayoutSidebarRightExpandFilled;

  return (
    <div className={classes.chatContainer}>
      <HudBox
        accentHue={240}
        background={false}
        bevel={10}
        cornerLength={160}
        corners={['tl', 'tr']}
        cornerThickness={4}
        particleCount={2}
        primaryHue={250}
        strokeWidth={1}
        style={
          {
            '--hud-background-opacity': 0.1,
            '--hud-glow-opacity': 0,
            '--hud-border-z-index': 10,
          } as React.CSSProperties
        }
      >
        <div className={classes.chat} ref={mouseTrackerRef}>
          <div className={classes.thread}>
            <Thread />
          </div>

          <TooltipIconButton
            className={classes.threadListToggle}
            color="primary"
            onClick={toggleThreadList}
            size="md"
            tooltip="Toggle thread list"
            variant="light"
          >
            <ToggleIcon size={24} />
          </TooltipIconButton>
          <AnimatePresence>
            {threadListOpen && (
              <motion.div
                animate={{ width: 'auto', opacity: 1 }}
                className={classes.threadList}
                exit={{ width: 0, opacity: 0 }}
                initial={{ width: 0, opacity: 0 }}
                key="thread-list"
                transition={{ duration: 0.2, ease: 'easeInOut' }}
              >
                <ThreadList />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </HudBox>
    </div>
  );
}
